import { useState } from 'react';
import { Card, Table, Tag, Input, Select, DatePicker, Button, Space, Typography, Drawer, Descriptions } from '@/shared/ui/primitives';
import { ReloadOutlined, EyeOutlined, ClearOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import { useAuditActions, useAuditLogs } from '../hooks/useOps';
import { formatDateTime } from '@/shared/utils/formatters';
import type { AuditLogEntry } from '../api/ops.api';

const { RangePicker } = DatePicker;

const INITIAL: any = {};

export default function AuditLogsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [size] = useState(25);
  const [filters, setFilters] = useState<any>(INITIAL);
  const [detail, setDetail] = useState<AuditLogEntry | null>(null);

  const params = { page, size, ...filters };
  const { data, isLoading, isFetching, refetch } = useAuditLogs(params) as any;
  const { data: actions } = useAuditActions();

  const setFilter = (patch: any) => { setFilters((f: any) => ({ ...f, ...patch })); setPage(0); };

  return (
    <div>
      <PageHeader title={t('auditLog.title')} subtitle={t('auditLog.subtitle')} />

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder={t('auditLog.filters.adminEmail')}
            allowClear
            style={{ width: 220 }}
            onChange={(e: any) => setFilter({ adminEmail: e.target.value || undefined })}
          />
          <Select
            placeholder={t('auditLog.filters.action')}
            allowClear
            style={{ width: 240 }}
            showSearch
            options={(Array.isArray(actions) ? actions : []).map((a: string) => ({ value: a, label: a }))}
            onChange={(v) => setFilter({ action: v })}
          />
          <Input
            placeholder={t('auditLog.filters.targetType')}
            allowClear
            style={{ width: 160 }}
            onChange={(e: any) => setFilter({ targetType: e.target.value || undefined })}
          />
          <Input
            placeholder={t('auditLog.filters.targetId')}
            allowClear
            type="number"
            style={{ width: 140 }}
            onChange={(e: any) => setFilter({ targetId: e.target.value || undefined })}
          />
          <RangePicker
            showTime
            onChange={(vals: any) => {
              setFilter({
                createdFrom: vals?.[0]?.toISOString(),
                createdTo: vals?.[1]?.toISOString(),
              });
            }}
          />
          <Button icon={<ClearOutlined />} onClick={() => { setFilters(INITIAL); setPage(0); }}>
            {t('common.reset')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            {t('dashboard.refresh')}
          </Button>
        </Space>
      </Card>

      <div className="ds-table-container">
        <Table
          dataSource={data?.content ?? []}
          loading={isLoading}
          rowKey="id"
          size="small"
          pagination={{
            current: (data?.currentPage ?? 0) + 1,
            pageSize: size,
            total: data?.totalItems ?? 0,
            showSizeChanger: false,
            onChange: (p) => setPage(p - 1),
            showTotal: (t2) => t('auditLog.entries', { count: t2 }),
          }}
          columns={[
            { title: t('dashboard.activity.when'), dataIndex: 'createdAt', width: 180, render: (v: string) => formatDateTime(v) },
            { title: t('dashboard.activity.admin'), dataIndex: 'adminEmail', width: 240, render: (v: string) => v || <Tag>{t('dashboard.activity.system')}</Tag> },
            { title: t('dashboard.activity.action'), dataIndex: 'action', width: 200, render: (v: string) => <Tag color="geekblue">{v}</Tag> },
            { title: t('dashboard.activity.target'), width: 170, render: (_: any, r: any) => r.targetType ? `${r.targetType}#${r.targetId ?? '—'}` : '—' },
            { title: t('dashboard.activity.summary'), dataIndex: 'summary', ellipsis: true },
            { title: t('auditLog.detail.ip'), dataIndex: 'ipAddress', width: 140, render: (v: string) => v || '—' },
            {
              title: '',
              width: 60,
              render: (_: any, r: AuditLogEntry) => (
                <Button type="link" icon={<EyeOutlined />} onClick={() => setDetail(r)} />
              ),
            },
          ]}
        />
      </div>

      <Drawer
        width={520}
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? t('auditLog.detail.heading', { id: detail.id }) : ''}
      >
        {detail && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('dashboard.activity.when')}>{formatDateTime(detail.createdAt)}</Descriptions.Item>
            <Descriptions.Item label={t('dashboard.activity.admin')}>{detail.adminEmail ?? t('dashboard.activity.system')} ({t('common.id')} {detail.adminId ?? '—'})</Descriptions.Item>
            <Descriptions.Item label={t('dashboard.activity.action')}><Tag color="geekblue">{detail.action}</Tag></Descriptions.Item>
            <Descriptions.Item label={t('dashboard.activity.target')}>{detail.targetType ? `${detail.targetType}#${detail.targetId ?? '—'}` : '—'}</Descriptions.Item>
            <Descriptions.Item label={t('dashboard.activity.summary')}>{detail.summary || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('auditLog.detail.ip')}>{detail.ipAddress || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('auditLog.detail.userAgent')}><Typography.Text copyable style={{ fontSize: 12 }}>{detail.userAgent || '—'}</Typography.Text></Descriptions.Item>
            <Descriptions.Item label={t('auditLog.detail.metadata')}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {detail.metadataJson ? JSON.stringify(JSON.parse(detail.metadataJson), null, 2) : '—'}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
}
